/**
 * Round 20 Y5 -- who signed a tolerance, and when, said on the page.
 *
 * The signer and date were only in the portfolio cell's hover title;
 * the tolerance page and the service page did not say. Each leg reads
 * its signer and date, and a leg not yet given reads "awaiting" with
 * the person who holds that seat.
 */
import { useTranslation } from "react-i18next";

import { seatHolder, useSeatHolders } from "shared/api/useSeatHolders";
import type { ToleranceStateRow } from "shared/tolerance/useToleranceState";

export function ToleranceSigners({
  row,
}: {
  row: ToleranceStateRow;
}): JSX.Element | null {
  const { t } = useTranslation();
  const seats = useSeatHolders();
  const legs = row.legs ?? [];
  if (legs.length === 0) return null;
  const awaitedName = (leg: string): string => {
    if (leg === "business_owner")
      return row.owner_name ?? t("toleranceSigners.noOwner");
    const holder = seatHolder(seats.data, leg);
    return holder.kind === "named"
      ? holder.name
      : holder.kind === "nobody"
        ? t("toleranceSigners.nobodyHolds")
        : t("toleranceSigners.holderUnknown");
  };
  return (
    <p
      data-testid={`tolerance-signers-${row.ibs_code}`}
      className="text-xs text-slate-600"
    >
      {legs.map((l, i) => (
        <span key={l.leg} data-testid={`tolerance-leg-${l.leg}`}>
          {i > 0 && " · "}
          {t(`toleranceSigners.leg.${l.leg}`)}:{" "}
          {l.name
            ? t("toleranceSigners.signed", { name: l.name, at: l.at ?? "" })
            : t("toleranceSigners.awaiting", { name: awaitedName(l.leg) })}
        </span>
      ))}
    </p>
  );
}
